import { NIL as NIL_UUID } from 'uuid';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums';
import {
  EventType,
  MediaType,
  MessageErrorType,
  MessageState,
} from '../../../../../../Chat.enum';
import { tChatEvent } from '../../../../../../Chat.types';
import { isLottieSticker } from '../../../../../../lottieSticker.functions';

/**
 * INT-1227: the photo albums of a thread. The list draws each album as one
 * bubble where its first photo stands, and skips the rest.
 */
export type tChatAlbums = {
  /** The album's first event in list order → its photos, in display order. */
  albumByAnchorId: ReadonlyMap<string, readonly tChatEvent[]>;
  /** Photos drawn inside an album (all but its anchor), and the album headers it absorbed. */
  hiddenIds: ReadonlySet<string>;
  /**
   * When a header still waiting for its photos stops waiting (epoch ms). The
   * list groups again then, so a header no photo followed shows its card.
   */
  recheckAt?: number;
};

export type tChatAlbumOptions = {
  channel?: eChannelType | string;
  /** Epoch ms the grouping runs at: decides whether a fresh header still waits. */
  now: number;
  /** Max gap between two photos of the same album from the contact. */
  inboundGapMs: number;
  /** Max gap between two photos of the same album sent from Leadsales. */
  outboundGapMs: number;
};

/**
 * WhatsApp sends an album as one header message plus one message per photo.
 * The Cloud API cannot represent the header, so Meta delivers it as an
 * `unsupported` message (subtype `unknown`, error 131051; 3050 for us) with no
 * media, and the photos as ordinary images. Nothing ties the photos to it but
 * time. Measured on production (2026-10-02, 4 h, 361 inbound and 149 echo
 * albums): the header's timestamp is 0-3 s before its first photo's, and its
 * webhook arrives before every photo in all but one album.
 */
const HEADER_LEAD_MS = 5_000;

/**
 * How long a header waits for its album (no photo after it yet, or only one)
 * before it shows its card, counted from its timestamp. Its webhook lands 1-2 s
 * after that timestamp and the first photo 1.8 s (p50) to 3.3 s (p90) after it.
 */
const HEADER_GRACE_MS = 10_000;

/** WhatsApp Web shows four tiles; the fourth says how many more there are. */
const ALBUM_VISIBLE_TILES = 4;

type tSide = 'inbound' | 'sent' | 'echo';

/**
 * Who a message comes from: the contact, someone in Leadsales (a person or a
 * bot: `createdBy` is set), or the business's own WhatsApp app (a coexistence
 * echo: from_me with no `createdBy`).
 */
function sideOf(event: tChatEvent): tSide {
  if (!event.message?.fromMe) return 'inbound';
  return event.createdBy ? 'sent' : 'echo';
}

function sameSender(a: tChatEvent, b: tChatEvent): boolean {
  const side = sideOf(a);
  if (side !== sideOf(b)) return false;
  if (side === 'sent') return a.createdBy === b.createdBy;
  if (side === 'inbound') {
    // Only a group chat has more than one inbound sender. The socket fills an
    // empty handle with the lead's, so an empty one matches any.
    const handleA = a.message?.participant?.handle;
    const handleB = b.message?.participant?.handle;
    return !handleA || !handleB || handleA === handleB;
  }
  return true;
}

function captionOf(event: tChatEvent): string {
  return event.content?.text?.trim() ?? '';
}

function timeOf(event: tChatEvent): number {
  return Date.parse(event.createdAt);
}

function hasQuote(event: tChatEvent): boolean {
  const quoted = event.content?.quotedEventID;
  return Boolean(quoted) && quoted !== NIL_UUID;
}

/**
 * A message that can stand in an album: one photo and nothing a tile could not
 * show. A failed photo keeps its own bubble, with its notice and retry, and so
 * does a photo that quotes a message or carries a link preview, an ad card or
 * an interactive payload.
 */
function isAlbumPhoto(event: tChatEvent): boolean {
  if (event.type !== EventType.MESSAGE) return false;
  if (!event.message || event.message.state === MessageState.ERROR) return false;

  const content = event.content;
  const attachments = content?.attachments;
  if (!attachments || attachments.length !== 1) return false;
  const [attachment] = attachments;
  if (attachment.mediaType !== MediaType.IMAGE) return false;
  // A sticker reaches the list as an IMAGE on the socket path; WhatsApp sends
  // stickers as WebP and photos as JPEG.
  if (attachment.mimeType === 'image/webp') return false;
  // INT-1702: meta-messenger stores Messenger and Instagram stickers with the
  // fixed mimeType `sticker`, since they carry no WebP to tell them apart.
  if (attachment.mimeType === 'sticker') return false;
  // INT-1708: an animated Lottie sticker also reaches the list as an IMAGE,
  // but it is a zip, not a photo.
  if (isLottieSticker(attachment)) return false;

  if (hasQuote(event)) return false;
  if (content?.linkPreview?.url) return false;
  if (content?.adReferral?.ad_id || content?.adReferral?.ads_context_data)
    return false;
  if (content?.interactive || content?.carousel) return false;
  return true;
}

/**
 * A 3050 placeholder with nothing attached: what Meta sends in place of an
 * album's header, but also for other content it cannot show (subtype `unknown`
 * covers both). So it is only hidden when an album, two photos or more,
 * follows it.
 */
function isAlbumHeader(event: tChatEvent): boolean {
  return (
    event.type === EventType.MESSAGE &&
    event.message?.state === MessageState.ERROR &&
    event.message.error === MessageErrorType.WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR &&
    !event.content?.attachments?.length
  );
}

type tCaptionRule =
  /** Photos from the contact grouped by time: a caption means a photo of its own. */
  | 'none'
  /** The composer puts the caption on the first file only. */
  | 'first'
  /** An album WhatsApp announced: one of its photos may carry the caption. */
  | 'one';

/** The photos that follow `events[start]` in the same album, `events[start]` included. */
function collectAlbum(
  events: tChatEvent[],
  start: number,
  rule: { gapMs: number; captions: tCaptionRule },
): tChatEvent[] {
  const first = events[start];
  const album = [first];
  let captions = captionOf(first) ? 1 : 0;

  for (let index = start + 1; index < events.length; index += 1) {
    const candidate = events[index];
    const previous = album[album.length - 1];
    if (!isAlbumPhoto(candidate) || !sameSender(first, candidate)) break;
    // "Reenviado" is a label on the album, so all its photos must share it.
    if (Boolean(candidate.message?.isForwarded) !== Boolean(first.message?.isForwarded))
      break;
    // Absolute: the socket appends without sorting, so a photo can sit before
    // its predecessor's timestamp.
    if (Math.abs(timeOf(candidate) - timeOf(previous)) > rule.gapMs) break;
    if (captionOf(candidate)) {
      if (rule.captions !== 'one' || captions > 0) break;
      captions += 1;
    }
    album.push(candidate);
  }
  return album;
}

/**
 * Display order: by time, which is the send order for Leadsales and the best
 * there is for WhatsApp, whose photos share one whole second and no index. The
 * captioned photo goes first among ties, and the list order breaks the rest.
 */
function displayOrder(album: tChatEvent[]): tChatEvent[] {
  return album
    .map((event, index) => ({ event, index }))
    .sort(
      (a, b) =>
        timeOf(a.event) - timeOf(b.event) ||
        Number(Boolean(captionOf(b.event))) - Number(Boolean(captionOf(a.event))) ||
        a.index - b.index,
    )
    .map(({ event }) => event);
}

/**
 * INT-1227: groups the photos of a thread into albums, the way WhatsApp Web
 * shows them, reading `events` in list order. Three sources, one shape:
 *
 * - WhatsApp API, from the contact or echoed from the business's own WhatsApp
 *   app: the photos after an album header, when its first photo comes within
 *   HEADER_LEAD_MS. The header is hidden only when two photos or more follow it.
 *   While it is fresh (HEADER_GRACE_MS) and has no photo after it yet, or only
 *   one with nothing after that, it is held back as the rest may still arrive.
 * - WhatsApp API, from the contact, with no header (forwarded batches, sends
 *   from WhatsApp Web): consecutive uncaptioned photos `inboundGapMs` apart.
 * - Any channel, from Leadsales: consecutive photos the same person or bot sent
 *   `outboundGapMs` apart, the caption on the first.
 *
 * An album has two photos or more. Echoes with no header stay as they are.
 */
export function groupChatAlbums(
  events: tChatEvent[],
  options: tChatAlbumOptions,
): tChatAlbums {
  const albumByAnchorId = new Map<string, readonly tChatEvent[]>();
  const hiddenIds = new Set<string>();
  let recheckAt: number | undefined;
  const isWhatsappApi = options.channel === eChannelType.WHATSAPP_API;

  const addAlbum = (album: tChatEvent[]) => {
    albumByAnchorId.set(album[0].id, displayOrder(album));
    for (const event of album.slice(1)) hiddenIds.add(event.id);
  };

  let index = 0;
  while (index < events.length) {
    const event = events[index];

    if (isWhatsappApi && isAlbumHeader(event)) {
      const next = events[index + 1];
      const headerAt = timeOf(event);
      const isFresh = options.now - headerAt < HEADER_GRACE_MS;
      const holdBack = () => {
        hiddenIds.add(event.id);
        const expiresAt = headerAt + HEADER_GRACE_MS;
        recheckAt = recheckAt === undefined ? expiresAt : Math.min(recheckAt, expiresAt);
      };

      if (
        next &&
        isAlbumPhoto(next) &&
        sameSender(event, next) &&
        timeOf(next) - headerAt >= -1_000 &&
        timeOf(next) - headerAt <= HEADER_LEAD_MS
      ) {
        const album = collectAlbum(events, index + 1, {
          gapMs: options.inboundGapMs,
          captions: 'one',
        });
        if (album.length >= 2) {
          hiddenIds.add(event.id);
          addAlbum(album);
        } else if (isFresh && index + 2 === events.length) {
          // One photo so far and nothing after it: the second may be on its way.
          holdBack();
        }
        index += 1 + album.length;
        continue;
      }
      if (!next && isFresh) holdBack();
      index += 1;
      continue;
    }

    if (isAlbumPhoto(event)) {
      const side = sideOf(event);
      let album: tChatEvent[] = [];
      if (side === 'inbound' && isWhatsappApi && !captionOf(event)) {
        album = collectAlbum(events, index, {
          gapMs: options.inboundGapMs,
          captions: 'none',
        });
      } else if (side === 'sent') {
        album = collectAlbum(events, index, {
          gapMs: options.outboundGapMs,
          captions: 'first',
        });
      }
      if (album.length >= 2) {
        addAlbum(album);
        index += album.length;
        continue;
      }
    }
    index += 1;
  }

  return { albumByAnchorId, hiddenIds, recheckAt };
}

export type tAlbumLayout = {
  /** 'pair': two tall tiles; 'trio': a wide tile over two; 'grid': two by two. */
  variant: 'pair' | 'trio' | 'grid';
  /** Photos drawn as tiles. */
  tiles: number;
  /** The "+N" on the last tile: the photos it stands for, its own included. */
  overflow: number;
};

/**
 * WhatsApp Web's album layouts: two photos side by side, three as one wide
 * photo over two, four or more as a 2×2 whose fourth tile says "+N" for the
 * photos from the fourth on (7 photos: three tiles and "+4").
 */
export function albumLayout(count: number): tAlbumLayout {
  if (count <= 2) return { variant: 'pair', tiles: count, overflow: 0 };
  if (count === 3) return { variant: 'trio', tiles: 3, overflow: 0 };
  const overflow = count > ALBUM_VISIBLE_TILES ? count - ALBUM_VISIBLE_TILES + 1 : 0;
  return { variant: 'grid', tiles: ALBUM_VISIBLE_TILES, overflow };
}

const STATE_PROGRESS: Readonly<Partial<Record<MessageState, number>>> = {
  [MessageState.CREATED]: 0,
  [MessageState.SEND_PENDING]: 1,
  [MessageState.SENDING]: 2,
  [MessageState.DECRYPTING]: 2,
  [MessageState.SENT]: 3,
  [MessageState.DELIVERED]: 4,
  [MessageState.READ]: 5,
};

/**
 * The ticks of an album: the photo furthest behind, as WhatsApp does. Two blue
 * checks only once every photo was read.
 */
export function albumState(album: readonly tChatEvent[]): MessageState | undefined {
  let state: MessageState | undefined;
  for (const event of album) {
    const candidate = event.message?.state;
    if (!candidate) continue;
    if (
      state === undefined ||
      (STATE_PROGRESS[candidate] ?? 0) < (STATE_PROGRESS[state] ?? 0)
    )
      state = candidate;
  }
  return state;
}

/** The album's caption: the one photo that carries text, if any. */
export function albumCaption(album: readonly tChatEvent[]): string | undefined {
  for (const event of album) {
    const text = event.content?.text;
    if (text?.trim()) return text;
  }
  return undefined;
}
